import { motion } from 'framer-motion';
import { EyeOff, User, PenLine, Check, Lock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { AnnouncementFormData, SenderDisplayMode } from '@/types/announcement';

const copy = {
  en: { title: 'How do you want to send this?', subtitle: "You're in control. Share your feelings your way.", anonymous: 'Stay Anonymous', named: 'Show My Name', nickname: 'Use a Nickname', confession: 'Anonymous Confession', confessionDesc: 'Private link only — no public listing', nickPlaceholder: 'e.g., Your secret admirer, A friend who cares...' },
  es: { title: '¿Cómo quieres enviar esto?', subtitle: 'Tú tienes el control. Comparte tus sentimientos a tu manera.', anonymous: 'Mantenerme Anónimo', named: 'Mostrar Mi Nombre', nickname: 'Usar un Apodo', confession: 'Confesión Anónima', confessionDesc: 'Solo enlace privado — sin listado público', nickPlaceholder: 'ej., Tu admirador secreto, Un amigo...' },
  fr: { title: 'Comment souhaitez-vous envoyer ceci ?', subtitle: 'Vous gardez le contrôle. Partagez vos sentiments à votre façon.', anonymous: 'Rester Anonyme', named: 'Montrer Mon Nom', nickname: 'Utiliser un Pseudonyme', confession: 'Confession Anonyme', confessionDesc: 'Lien privé uniquement — pas de publication', nickPlaceholder: 'ex., Votre admirateur secret, Un ami...' },
  pt: { title: 'Como você quer enviar isso?', subtitle: 'Você está sempre no controle. Compartilhe seus sentimentos do seu jeito.', anonymous: 'Ficar Anônimo', named: 'Mostrar Meu Nome', nickname: 'Usar um Apelido', confession: 'Confissão Anônima', confessionDesc: 'Apenas link privado — sem listagem pública', nickPlaceholder: 'ex., Seu admirador secreto, Um amigo...' },
  ar: { title: 'كيف تريد إرسال هذا؟', subtitle: 'أنت دائماً في السيطرة. شارك مشاعرك بطريقتك.', anonymous: 'ابقَ مجهولاً', named: 'أظهر اسمي', nickname: 'استخدم لقباً', confession: 'اعتراف مجهول', confessionDesc: 'رابط خاص فقط — بدون نشر عام', nickPlaceholder: 'مثال: معجبك السري، صديق يهتم...' },
  ja: { title: 'どのように送りますか？', subtitle: 'あなたが常にコントロールしています。あなたの方法で気持ちを伝えましょう。', anonymous: '匿名のまま', named: '名前を表示', nickname: 'ニックネームを使う', confession: '匿名の告白', confessionDesc: 'プライベートリンクのみ — 公開リストなし', nickPlaceholder: '例：秘密の崇拝者、気にかけている友人...' },
  ko: { title: '어떻게 보내고 싶으신가요?', subtitle: '항상 당신이 통제합니다. 당신만의 방식으로 감정을 나누세요.', anonymous: '익명으로 보내기', named: '이름 표시하기', nickname: '별명 사용하기', confession: '익명 고백', confessionDesc: '비공개 링크만 — 공개 목록 없음', nickPlaceholder: '예: 비밀 연모자, 당신을 아끼는 친구...' },
  id: { title: 'Bagaimana Anda ingin mengirim ini?', subtitle: 'Anda selalu memegang kendali. Bagikan perasaan Anda dengan cara Anda.', anonymous: 'Tetap Anonim', named: 'Tampilkan Nama Saya', nickname: 'Gunakan Nama Panggilan', confession: 'Pengakuan Anonim', confessionDesc: 'Hanya tautan pribadi — tanpa daftar publik', nickPlaceholder: 'contoh: Pengagum rahasiamu, Teman yang peduli...' },

  hi: { title: 'आप इसे कैसे भेजना चाहते हैं?', subtitle: "You're in control. Share your feelings your way.", anonymous: 'गुमनाम रहें', named: 'मेरा नाम दिखाएँ', nickname: 'एक उपनाम का उपयोग करें', confession: 'गुमनाम स्वीकारोक्ति', confessionDesc: 'केवल निजी लिंक — कोई सार्वजनिक सूची नहीं', nickPlaceholder: 'उदाहरण के लिए, आपका गुप्त प्रशंसक, एक दोस्त जो परवाह करता है...' },
};

const identityOptions: { value: SenderDisplayMode; icon: typeof EyeOff; hasDescription?: boolean }[] = [
  { value: 'named', icon: User },
  { value: 'nickname', icon: PenLine },
];

interface IdentityChoiceStepProps {
  formData: AnnouncementFormData;
  updateFormData: (updates: Partial<AnnouncementFormData>) => void;
}

export function IdentityChoiceStep({ formData, updateFormData }: IdentityChoiceStepProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const meta = formData.metadata || {};
  const selected = meta.senderDisplayMode || null;

  const handleSelect = (mode: SenderDisplayMode) => {
    updateFormData({
      metadata: { ...meta, senderDisplayMode: mode },
    });
  };

  const labelMap: Record<SenderDisplayMode, string> = {
    anonymous: c.anonymous,
    named: c.named,
    nickname: c.nickname,
    confession: c.confession,
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
          <EyeOff className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-2">{c.title}</h2>
        <p className="text-muted-foreground max-w-md mx-auto">{c.subtitle}</p>
      </div>

      <div className="max-w-md mx-auto space-y-3">
        {identityOptions.map((option) => {
          const isSelected = selected === option.value;
          const Icon = option.icon;
          return (
            <motion.button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full relative flex items-center gap-4 p-5 rounded-xl border-2 transition-all text-left',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 hover:bg-muted/50'
              )}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              {isSelected && (
                <motion.div className="absolute top-3 right-3" initial={{ scale: 0 }} animate={{ scale: 1 }}>
                  <Check className="h-4 w-4 text-primary" />
                </motion.div>
              )}
              <div className={cn(
                'flex items-center justify-center h-10 w-10 rounded-full',
                isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              )}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className={cn('text-base font-medium', isSelected ? 'text-primary' : 'text-foreground')}>
                  {labelMap[option.value]}
                </span>
                {option.hasDescription && (
                  <span className="text-xs text-muted-foreground mt-0.5">{c.confessionDesc}</span>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Nickname input */}
      {selected === 'nickname' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="max-w-md mx-auto"
        >
          <Label htmlFor="senderNickname" className="text-sm text-muted-foreground mb-2 block">
            {c.nickname}
          </Label>
          <Input
            id="senderNickname"
            placeholder={c.nickPlaceholder}
            value={meta.senderNickname || ''}
            onChange={(e) =>
              updateFormData({
                metadata: { ...meta, senderDisplayMode: 'nickname', senderNickname: e.target.value },
              })
            }
            className="text-lg py-5"
          />
        </motion.div>
      )}

      {/* Confession privacy note */}
      {selected === 'confession' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="max-w-md mx-auto"
        >
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
            <Lock className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              {language === 'en'
                ? 'Your confession will only be accessible via a private link that you share directly. It won\'t appear in any public feed or search.'
                : c.confessionDesc}
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
